import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { UserCheck, UserX, User, UserCog, Edit } from 'lucide-react';
import DataTable from '../components/DataTable';
import PageContainer from '../components/PageContainer';
import { fetchAllUsers } from '../store/slices/userSlice';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.color || '#6b7280'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  flex-shrink: 0;
`;

// Removed PageContainer - now using reusable PageContainer component from components/PageContainer.jsx

const Title = styled.h2`
  margin: 0 0 1.5rem 0;
  color: #2c3e50;
  font-size: 1.8rem;
`;

const Description = styled.p`
  color: #6b7280;
  font-size: 0.95rem;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
  font-size: 1rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'assigned': return '#dbeafe';
      case 'unassigned': return '#f3f4f6';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'assigned': return '#1e40af';
      case 'unassigned': return '#6b7280';
      default: return '#6b7280';
    }
  }};
`;


const Admin = () => {
  const dispatch = useDispatch();
  const { user: authUser } = useAuth();
  const currentUser = authUser; // Use authenticated user from AuthContext
  const allUsers = useSelector(state => state.user?.allUsers);
  const [loans, setLoans] = useState([]);
  const [selectedLoanIds, setSelectedLoanIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const getInitials = (fullName) => {
    if (!fullName || fullName === '-') return '';
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const getUserColor = (userName) => {
    if (!userName || userName === '-') return '#6b7280';
    const user = allUsers.find(u => u.full_name === userName);
    return user?.avatar_color || '#6b7280';
  };

  const loadLoans = async () => {
    // Don't show loading state if we already have data (prevents flicker on refresh)
    if (loans.length === 0) {
      setLoading(true);
    }
    
    try {
      // Use api service which automatically includes auth token
      const response = await api.get(`/admin/loans/unified${showArchived ? '?include_archived=true' : ''}`);
      const data = response.data;
      if (data.success) {
        setLoans(data.loans);
      }
    } catch (error) {
      console.error('Error loading loans:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search loans
  const filteredLoans = useMemo(() => {
    let filtered = loans;

    // Filter by assignment status
    if (statusFilter === 'unassigned') {
      filtered = filtered.filter(loan => loan.assignment_status === 'unassigned');
    } else if (statusFilter === 'assigned') {
      filtered = filtered.filter(loan => loan.assignment_status === 'assigned');
    }

    // Filter by assignee
    if (assigneeFilter !== 'all') {
      filtered = filtered.filter(loan => loan.assigned_to_name === assigneeFilter);
    }

    // Search by loan ID or borrower name (partial matches)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(loan => {
        const loanId = (loan.loan_id || '').toString().toLowerCase();
        const borrowerName = (loan.borrower_name || '').toLowerCase();
        return loanId.includes(query) || borrowerName.includes(query);
      });
    }

    return filtered;
  }, [loans, statusFilter, assigneeFilter, searchQuery]);

  // Count for each status
  const statusCounts = useMemo(() => ({
    all: loans.length,
    unassigned: loans.filter(l => l.assignment_status === 'unassigned').length,
    assigned: loans.filter(l => l.assignment_status === 'assigned').length
  }), [loans]);

  // Get unique assignees for filter
  const assignees = useMemo(() => {
    const names = [...new Set(loans
      .filter(l => l.assigned_to_name && l.assigned_to_name !== '-')
      .map(l => l.assigned_to_name))];
    return names.sort();
  }, [loans]);

  useEffect(() => {
    loadLoans();
    // Fetch users if not already loaded
    if (allUsers.length === 0) {
      dispatch(fetchAllUsers());
    }
  }, [dispatch, allUsers.length, showArchived]);

  // Expose to panel via global state
  useEffect(() => {
    window.adminSelectedLoans = selectedLoanIds;
    window.adminReloadLoans = loadLoans;
    window.adminStatusFilter = statusFilter;
    window.adminAssigneeFilter = assigneeFilter;
    window.adminSearchQuery = searchQuery;
    window.adminStatusCounts = statusCounts;
    window.adminAssignees = assignees;
    window.adminShowArchived = showArchived;
    window.adminSetShowArchived = setShowArchived;
    window.adminClearSelection = () => {
      setSelectedLoanIds([]);
    };
    window.adminApplyFilters = () => {
      // Trigger re-render by updating state
      setStatusFilter(window.adminStatusFilter);
      setAssigneeFilter(window.adminAssigneeFilter);
      setSearchQuery(window.adminSearchQuery);
    };
  }, [selectedLoanIds, statusFilter, assigneeFilter, searchQuery, statusCounts, assignees, showArchived]);

  // Transform loans data for the table
  const tableData = filteredLoans.map(loan => ({
    loan_id: loan.loan_id,
    borrower_name: loan.borrower_name || 'N/A',
    loan_amount: loan.loan_amount || 'N/A',
    assignment_status: loan.assignment_status || 'unassigned',
    assigned_to_name: loan.assigned_to_name || '-',
    assigned_by_name: loan.assigned_by_name || '-',
    assigned_at: loan.assigned_at ? new Date(loan.assigned_at).toLocaleDateString() : '-',
    data_status: loan.data_status || 'active',
    is_protected: loan.is_protected || false,
    has_data: loan.has_data || false
  }));

  const columns = [
    {
      field: 'assignment_status',
      header: 'Status',
      width: '80px',
      align: 'center',
      render: (row) => {
        const isAssigned = row.assignment_status === 'assigned';
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {isAssigned ? (
              <UserCheck size={20} color="#10b981" />
            ) : (
              <UserX size={20} color="#ef4444" />
            )}
          </div>
        );
      }
    },
    {
      field: 'loan_id',
      header: 'ID',
      width: '10%',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{row.loan_id}</span>
          {row.data_status === 'archived' && (
            <span style={{
              fontSize: '0.65rem',
              padding: '2px 6px',
              background: '#fef3c7',
              color: '#92400e',
              borderRadius: '4px',
              fontWeight: '600'
            }}>
              ARCHIVED
            </span>
          )}
        </div>
      )
    },
    {
      field: 'borrower_name',
      header: 'Borrower',
      width: 'auto'
    },
    {
      field: 'loan_amount',
      header: 'Amount',
      width: '140px',
      align: 'right',
      render: (row) => {
        if (!row.loan_amount || row.loan_amount === 'N/A') return '-';
        const amount = parseFloat(row.loan_amount.replace(/[^0-9.-]+/g, ''));
        if (isNaN(amount)) return row.loan_amount;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(amount);
      }
    },
    {
      field: 'assigned_to_name',
      header: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Assigned To">
          <UserCheck size={16} color="#000000" />
        </div>
      ),
      width: '60px',
      align: 'center',
      render: (row) => {
        if (row.assigned_to_name === '-') return '-';
        return (
          <Avatar 
            color={getUserColor(row.assigned_to_name)}
            title={row.assigned_to_name}
          >
            {getInitials(row.assigned_to_name)}
          </Avatar>
        );
      }
    },
    {
      field: 'assigned_by_name',
      header: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Assigned By">
          <UserCog size={16} color="#000000" />
        </div>
      ),
      width: '60px',
      align: 'center',
      render: (row) => {
        if (row.assigned_by_name === '-') return '-';
        return (
          <Avatar 
            color={getUserColor(row.assigned_by_name)}
            title={row.assigned_by_name}
          >
            {getInitials(row.assigned_by_name)}
          </Avatar>
        );
      }
    },
    {
      field: 'actions',
      header: 'Actions',
      width: '80px',
      align: 'center',
      render: (row) => {
        if (!row.has_data) {
          return (
            <button
              onClick={() => {
                // TODO: Navigate to document upload/scanning for this loan
                console.log('Edit loan:', row.loan_id);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
                background: '#3a3a3a',
                border: '1px solid #4a4a4a',
                borderRadius: '4px',
                color: '#e0e0e0',
                cursor: 'pointer',
                fontSize: '0.75rem',
                transition: 'all 0.2s'
              }}
              title="Add documents to this loan"
            >
              <Edit size={14} />
            </button>
          );
        }
        return '-';
      }
    }
  ];

  // Check if user is admin or super_admin
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  if (!isAdmin) {
    return (
      <PageContainer>
        <Title>Access Denied</Title>
        <Description>
          You don't have permission to access this page. Only administrators can manage loan assignments.
        </Description>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Loan Assignment Management</Title>
      <Description>
        Assign loans to team members for review. Use the left panel to search, filter, and assign loans. Select loans using checkboxes or "Select All" to select all {loans.length} loans.
      </Description>

      {loading ? (
        <LoadingMessage>Loading loans...</LoadingMessage>
      ) : (
        <>
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            background: selectedLoanIds.length > 0 ? '#f3f4f6' : '#fafafa',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
            color: selectedLoanIds.length > 0 ? '#4b5563' : '#9ca3af',
            fontSize: '0.875rem',
            fontWeight: '500',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              {selectedLoanIds.length} {selectedLoanIds.length === 1 ? 'loan' : 'loans'} selected
              {selectedLoanIds.length > 0 && (
                <span style={{ marginLeft: '0.5rem' }}>
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedLoanIds([]);
                    }}
                    style={{
                      color: '#60a5fa',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      fontWeight: '400'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#93c5fd';
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#60a5fa';
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    Clear selection
                  </a>
                </span>
              )}
            </span>
            {filteredLoans.length !== loans.length && (
              <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                Showing {filteredLoans.length} of {loans.length} loans
              </span>
            )}
          </div>
              <DataTable
                data={tableData}
                columns={columns}
                keyField="loan_id"
                rowKey="loan_id"
                selectable={true}
                selectedRows={selectedLoanIds}
                onSelectionChange={setSelectedLoanIds}
                pagination={true}
                pageSize={50}
                pageSizeOptions={[50, 100, 250]}
                showSelectAll={true}
                getRowStyle={(row) => {
                  // Gray background for unscanned loans
                  if (!row.has_data) {
                    return {
                      backgroundColor: '#f5f5f5',
                      opacity: 0.7
                    };
                  }
                  return {};
                }}
              />
        </>
      )}
    </PageContainer>
  );
};

export default Admin;
